import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import useFetchSingleChallenge from '@pages/challengeGroups/challenge/_hooks/useFetchSingleChallenge';
import dayjs from 'dayjs';
import Breadcrumb from '@components/breadcrumb/Breadcrumb';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import LinkButton from '@components/button/LinkButton';
import DataCheck from 'lib/src/components/common/DataCheck';
import Description from 'lib/src/components/typography/Description';
import Title from 'lib/src/components/typography/Title';
import { formatMoney, isEmpty } from 'lib/src/utils/generic';
import { useParams } from 'react-router';
import config from '../../../../src/config';
import { assessmentBreadcrumbs } from '../../../constants/breadcrumbItems';
import useFetchSubmission from './hooks/useFetchSubmission';
import useHasRole from 'lib/src/hooks/useHasRole';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import SubmissionsScoresTable from './SubmissionScoresTable';
import { site } from 'lib/src/shared/enums/site';
interface Params {
    id: string;
}

const Submission = () => {
    const { id } = useParams<Params>();

    const isSuperAdmin = useHasRole(userRoles.superAdmin);

    const {
        submission,
        isFetching: submissionIsFetching,
        fetchError: submissionFetchError,
    } = useFetchSubmission(+id);

    const {
        challenge: assessmentChallenge,
        isFetching: assessmentChallengeIsFetching,
        fetchError: assessmentChallengeFetchError,
    } = useFetchSingleChallenge(submission.challengeID);

    assessmentBreadcrumbs[2].name =
        assessmentChallenge == null ? 'Loading' : assessmentChallenge.name;
    assessmentBreadcrumbs[2].link = `/assessments/submissions/${assessmentChallenge?.id || ''}`;

    const canBeAssessed =
        assessmentChallenge.readyForAssessmentsSetOn &&
        !assessmentChallenge.assessmentsCompleteSetOn;

    return (
        <DataCheck
            dataExists={!isEmpty(submission)}
            isFetching={submissionIsFetching || assessmentChallengeIsFetching}
            error={submissionFetchError || assessmentChallengeFetchError}
        >
            <Breadcrumb
                items={assessmentBreadcrumbs}
                title={submission.companyName || 'Submission Details'}
            />
            <Title>Submission Details</Title>
            <Description>Submitted by {submission.companyName}</Description>

            {(canBeAssessed || assessmentChallenge.site !== site.GA) && (
                <ContentBlock>
                    <ContentRow>
                        <ButtonRow>
                            {canBeAssessed && (
                                <LinkButton
                                    href={`/assessments/submissions/assessment/${
                                        submission.hasUserAssessed ? 'edit' : 'assess'
                                    }/${submission.id}`}
                                    color="green"
                                    icon="pencil"
                                    target="_blank"
                                >
                                    {submission.hasUserAssessed
                                        ? 'Edit Assessment'
                                        : 'Assess Submission'}
                                </LinkButton>
                            )}
                            {assessmentChallenge.site !== site.GA && (
                                <LinkButton
                                    href={`/assessments/edit-application/${submission.id}`}
                                    color="yellow"
                                    icon="pencil"
                                >
                                    Edit Organisation Details
                                </LinkButton>
                            )}
                        </ButtonRow>
                    </ContentRow>
                </ContentBlock>
            )}

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="KTN Submission Summary">
                        <p>{submission.ktnEntrySummary}</p>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>
            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Company / Organisation name">
                        <p>{submission.companyName}</p>
                    </ContentItem>
                    <ContentItem label="Website">
                        <p>{submission.url}</p>
                    </ContentItem>
                    <ContentItem label="Application date">
                        <p>
                            {submission.submittedOn ? (
                                <p>{dayjs(submission.submittedOn).format('DD/MM/YYYY')}</p>
                            ) : (
                                ''
                            )}
                        </p>
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem label="Address line 1">
                        <p>{submission.addressLine1}</p>
                    </ContentItem>
                    <ContentItem label="Address line 2">
                        <p>{submission.addressLine2}</p>
                    </ContentItem>
                    <ContentItem label="Town/City">
                        <p>{submission.townCity}</p>
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem label="Postcode">
                        <p>{submission.postcode}</p>
                    </ContentItem>
                    <ContentItem label="County">
                        <p>{submission.county}</p>
                    </ContentItem>
                    <ContentItem label="Region">
                        <p>{submission.region}</p>
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem label="Contact Name">
                        <p>{submission.contactName}</p>
                    </ContentItem>
                    <ContentItem label="Job title">
                        <p>{submission.jobTitle}</p>
                    </ContentItem>
                    <ContentItem label="Email">
                        <p>{submission.email}</p>
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem label="Telephone">
                        <p>{submission.telephone}</p>
                    </ContentItem>
                    <ContentItem label="Company registration">
                        <p>{submission.companyReg}</p>
                    </ContentItem>
                    <ContentItem label="Turnover">
                        <p>
                            {!!submission.turnover ? '£' + formatMoney(+submission.turnover) : '-'}
                        </p>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Idea summary">
                        <p>{submission.ideaSummary}</p>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Technology readiness">
                        <p>{submission.trlDescription}</p>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Intellectual property">
                        <p>{submission.ipStatusDescription}</p>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Is this a partial solution?">
                        <p>{submission.isPartialSolution ? 'Yes' : 'No'}</p>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Extra attachments">
                        <ul>
                            {submission.attachmentS3Keys?.map((key, i) => (
                                <li>
                                    <a
                                        href={`${config.S3_URL}/${key}`}
                                        key={i}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {key.slice(key.lastIndexOf('/') + 1)}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </ContentItem>
                </ContentRow>
            </ContentBlock>

            {isSuperAdmin && (
                <>
                    <Title>Assessments</Title>
                    <ContentBlock>
                        <SubmissionsScoresTable
                            applicationID={submission.id}
                            challenge={assessmentChallenge}
                        />
                    </ContentBlock>
                </>
            )}
        </DataCheck>
    );
};

export default Submission;
